import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';

import { ICampaignObjectiveProgress } from 'core';

export interface IPromotionMissionDetailsModalData {
  title: string;
  description: string;
  statusLabel: string;
  objectiveProgress: ICampaignObjectiveProgress | null;
  entriesCount: number;
  maxEntriesPerUser: number | null;
  remainingEntries: number | null;
  endDate: string | null;
  progress: number | null;
}

interface IMissionTimeRemainingLabel {
  key: string;
  params: Record<string, number>;
}

@Component({
  selector: 'app-promotion-mission-details-modal',
  imports: [TranslatePipe],
  templateUrl: './promotion-mission-details-modal.component.html',
  styleUrl: './promotion-mission-details-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PromotionMissionDetailsModalComponent {
  readonly #modal = inject(NzModalRef);
  readonly modalData = inject<IPromotionMissionDetailsModalData>(NZ_MODAL_DATA);

  readonly timeRemaining = computed<IMissionTimeRemainingLabel>(() => {
    const endDate = this.modalData.endDate;
    const endTimestamp = endDate ? new Date(endDate).getTime() : NaN;

    if (!Number.isFinite(endTimestamp)) {
      return { key: 'promotions.missions.noEndDate', params: {} };
    }

    const remainingMs = endTimestamp - Date.now();

    if (remainingMs <= 0) {
      return { key: 'promotions.campaignDetails.statuses.expired', params: {} };
    }

    const days = Math.floor(remainingMs / 86_400_000);
    if (days >= 1) {
      return {
        key: days === 1 ? 'promotions.missions.daysLeft' : 'promotions.missions.daysLeft_other',
        params: { count: days }
      };
    }

    const hours = Math.floor(remainingMs / 3_600_000);
    if (hours >= 1) {
      return {
        key: hours === 1 ? 'promotions.missions.hoursLeft' : 'promotions.missions.hoursLeft_other',
        params: { count: hours }
      };
    }

    return { key: 'promotions.missions.lessThanHourLeft', params: {} };
  });

  public close(): void {
    this.#modal.destroy();
  }
}
